import * as React from 'react';

import { CareerAcademy_Variant as CareerAcademyVariant } from '__generated__/graphql-types';

import Imgix from 'js/components/Imgix';

import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';
import Skills from 'bundles/unified-career-academy/components/Skills';
import EnterpriseHeroNotConfigurable from 'bundles/unified-career-academy/components/modules/EnterpriseHeroNotConfigurable';
import { EnterpriseMyRecentActivityNotConfigurableModule } from 'bundles/unified-career-academy/components/modules/EnterpriseMyRecentActivityNotConfigurableModule';
import CareerCardCredentialsNoLink from 'bundles/unified-career-academy/components/modules/careers/CareerCardCredentialsNoLink';
import CareerCardDescription from 'bundles/unified-career-academy/components/modules/careers/CareerCardDescription';
import CareerCardModule from 'bundles/unified-career-academy/components/modules/careers/CareerCardModule';
import CareerCardStats from 'bundles/unified-career-academy/components/modules/careers/CareerCardStats';
import CareersFooter from 'bundles/unified-career-academy/components/modules/careers/CareersFooter';
import CareersModule from 'bundles/unified-career-academy/components/modules/careers/CareersModule';
import TestimonialsModule from 'bundles/unified-career-academy/components/modules/testimonials/TestimonialsModule';
import TestimonialAuthor from 'bundles/unified-career-academy/components/modules/testimonials/quote/TestimonialAuthor';
import TestimonialQuoteModule from 'bundles/unified-career-academy/components/modules/testimonials/quote/TestimonialQuoteModule';
import { ValuePropBanner } from 'bundles/unified-career-academy/components/modules/value-props/ValuePropBanner';
import ValuePropCard from 'bundles/unified-career-academy/components/modules/value-props/ValuePropCard';
import ValuePropsModule from 'bundles/unified-career-academy/components/modules/value-props/ValuePropsModule';
import { usePageContent } from 'bundles/unified-career-academy/contexts/PageContentContext';
import { filterEnterpriseCertificates } from 'bundles/unified-career-academy/utils/helpers';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';

import GoogleLogoFull from 'images/enterprise/google_logo_full.png';

import _t from 'i18n!nls/career-academy';

export const EnterpriseCareerAcademyFreemiumPageContent: React.FC = () => {
  const pageContent = usePageContent();
  const program = useProgram();
  const features = useEnterpriseProductFeatures();
  const thirdPartyOrganization = useThirdPartyOrganization();

  if (!pageContent || !program || !thirdPartyOrganization) {
    return null;
  }

  return (
    <React.Fragment>
      <EnterpriseHeroNotConfigurable />
      <EnterpriseMyRecentActivityNotConfigurableModule />
      <ValuePropsModule
        title={pageContent.valuePropTitle}
        subtitle={pageContent.valuePropSubtitle}
        valuePropCards={pageContent.valuePropValuesCollection?.items.map((value) => (
          <ValuePropCard
            title={value?.title}
            subtitle={value?.subtitle}
            description={value?.description}
            iconName={value?.iconName}
          />
        ))}
        valuePropBanner={
          <ValuePropBanner
            title={_t(
              'Of Google Professional Certificate completers in the United States report an improvement in their career trajectory (e.g. new job or career, promotion or raise) within 6 months of certificate completion'
            )}
            description={_t('*Based on program graduate survey responses, United States 2021.')}
            valuePropTitle={_t('Top Instructor')}
            valuePropImg={
              <Imgix
                src={GoogleLogoFull}
                alt={_t('Google Logo image')}
                maxHeight={50}
                maxWidth={155}
                css={{ marginTop: '8px' }}
              />
            }
          />
        }
      />
      <CareersModule
        title={pageContent.careersTitle}
        subtitle={pageContent.careersSubtitle}
        footer={
          features?.enableCareerAcademyLaborMarketData && <CareersFooter content={pageContent.careersFootnotes} />
        }
        careerCardsWithRoles={pageContent.careersCardsCollection?.items.map((card) => {
          const googleCredential = card?.programsCollection?.items.filter((cert) =>
            cert?.partnerInformationCollection?.items.some(
              (partner) => partner?.partnerSlug?.toLocaleLowerCase() === 'google'
            )
          );

          return {
            role: card?.jobCategory,
            card: (
              <CareerCardModule
                roleSlug={card?.slug}
                title={card?.title}
                url={card?.slug && `/career-academy/programs/${program?.metadata.slug}/roles/${card.slug}`}
                image={card?.image}
                description={<CareerCardDescription content={card?.description} />}
                skills={<Skills skills={card?.skills} />}
                careerStats={
                  features?.enableCareerAcademyLaborMarketData && <CareerCardStats stats={card?.careerStats} />
                }
                credentials={
                  <CareerCardCredentialsNoLink
                    credentials={filterEnterpriseCertificates(googleCredential, CareerAcademyVariant.GrowWithGoogle)}
                  />
                }
              />
            ),
          };
        })}
      />
      {features?.enableCareerAcademyTestimonialsComponent && (
        <TestimonialsModule
          title={pageContent.testimonialsTitle}
          subtitle={pageContent.testimonialsSubtitle}
          testimonials={
            pageContent.testimonialsUserTestimonialCollection?.items
              .filter((testimonial) => !testimonial?.videoEmbedUrl)
              .map((testimonial, index) => (
                <TestimonialQuoteModule
                  index={index}
                  text={testimonial?.text}
                  author={
                    <TestimonialAuthor
                      authorName={testimonial?.authorName}
                      authorTitle={testimonial?.authorTitle}
                      imageUrl={testimonial?.image?.url}
                    />
                  }
                />
              )) ?? []
          }
        />
      )}
      <CourseraMetatags
        metaNameAndProperties={{
          title: program?.metadata.name,
        }}
      />
    </React.Fragment>
  );
};
